/**
 * Teacher marketplace catalog — packs + Skylite OS “vinyl box” shelf.
 * Keep copy here so the shop UI stays a thin renderer.
 */

export const TRAINING_PACKS = [
  {
    id: 'cloud-security',
    title: 'Cloud Security',
    tabLabel: 'Cloud Security',
    // Temporary: coming_soon while filming (TEMP_HIDE_CLOUD_SECURITY_PACK). Restore to 'available' after.
    status: 'coming_soon',
    accent: {
      tab: '#0ea5e9',
      panel: 'linear-gradient(165deg, #0c4a6e 0%, #082f49 55%, #020617 100%)',
      chip: 'rgba(14, 165, 233, 0.22)',
      border: 'rgba(56, 189, 248, 0.45)'
    },
    summary:
      'Cloud models, shared responsibility, IAM, networking, encryption, and misconfiguration labs — then Cloud Security 201 hands-on CLI/console practice.',
    curriculum: [
      'Cloud Security 101 — Models (IaaS / PaaS / SaaS)',
      'Shared responsibility & IAM / MFA',
      'VPC, security groups & perimeter thinking',
      'Data protection, TLS, KMS & FERPA themes',
      'Misconfigurations, logging & hardening',
      'Review + assessment (80%+)',
      'Cloud Security 201 — CLI/console labs (IAM, S3, network, KMS, audit)'
    ],
    dashboardSections: ['cloud-security', 'cloud-security-201'],
    levelRangeHint: 'Levels 135–150'
  },
  {
    id: 'ai',
    title: 'AI',
    tabLabel: 'AI',
    status: 'coming_soon',
    accent: {
      tab: '#f59e0b',
      panel: 'linear-gradient(165deg, #78350f 0%, #451a03 55%, #1c1917 100%)',
      chip: 'rgba(245, 158, 11, 0.22)',
      border: 'rgba(251, 191, 36, 0.45)'
    },
    summary:
      'Practical AI literacy for classrooms: how models work, prompt craft, evaluation, and responsible use — without turning students into uncritical copy-pasters.',
    curriculum: [
      'What a model is (and is not)',
      'Prompt patterns for school work',
      'Checking outputs for accuracy & bias',
      'Citation, originality & academic honesty',
      'Classroom policies & safe tool use',
      'Mini projects + checkpoint quiz'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon'
  },
  {
    id: 'ai-cyber',
    title: 'AI Cyber',
    tabLabel: 'AI Cyber',
    status: 'coming_soon',
    accent: {
      tab: '#d946ef',
      panel: 'linear-gradient(165deg, #701a75 0%, #4a044e 55%, #1e0533 100%)',
      chip: 'rgba(217, 70, 239, 0.22)',
      border: 'rgba(232, 121, 249, 0.45)'
    },
    summary:
      'Where AI meets security operations: detecting AI-assisted phishing, abusing vs defending with automation, and red-team / blue-team habits for student labs.',
    curriculum: [
      'AI-assisted social engineering awareness',
      'Automating triage vs over-trusting tools',
      'Prompt injection & agent risk (intro)',
      'Logging, evidence & escalation habits',
      'Defensive playbooks for school networks',
      'Capstone scenario lab'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon'
  },
  {
    id: 'ai-agents',
    title: 'AI Agents',
    tabLabel: 'AI Agents',
    status: 'coming_soon',
    accent: {
      tab: '#22d3ee',
      panel: 'linear-gradient(165deg, #155e75 0%, #0e7490 45%, #083344 100%)',
      chip: 'rgba(34, 211, 238, 0.22)',
      border: 'rgba(103, 232, 249, 0.45)'
    },
    summary:
      'Two-tier agent class: Intro (no Docker) — predetermined browser labs on what agents are, tools vs chat, and safe classroom workflows. Advanced (Docker) — agentic loops, sandboxed tool use, and multi-step agent runtimes.',
    curriculum: [
      'Intro — agents vs chatbots (predetermined, browser-only)',
      'Tools, memory & guardrails without containers',
      'Fixed lab scripts so every student gets the same outcome',
      'Advanced — agentic loops & multi-step planning (Docker)',
      'Sandboxed tool calling & safe runtime isolation',
      'Capstone: build / evaluate a classroom agent workflow'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon — Intro (no Docker) → Advanced (Docker)'
  },
  {
    id: 'blockchain',
    title: 'Blockchain',
    tabLabel: 'Blockchain',
    status: 'coming_soon',
    accent: {
      tab: '#10b981',
      panel: 'linear-gradient(165deg, #064e3b 0%, #022c22 55%, #052e16 100%)',
      chip: 'rgba(16, 185, 129, 0.22)',
      border: 'rgba(52, 211, 153, 0.45)'
    },
    summary:
      'Demystify ledgers, wallets, and consensus — enough to spot hype, understand integrity use-cases, and talk about risk without mining lectures.',
    curriculum: [
      'Ledgers, hashes & immutability (plain English)',
      'Wallets, keys & custody basics',
      'Public vs permissioned chains',
      'Smart contracts — promises vs pitfalls',
      'Scams, rug pulls & verification habits',
      'Short labs + assessment'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon'
  },
  {
    id: 'quantum',
    title: 'Quantum',
    tabLabel: 'Quantum',
    status: 'coming_soon',
    accent: {
      tab: '#818cf8',
      panel: 'linear-gradient(165deg, #312e81 0%, #1e1b4b 55%, #020617 100%)',
      chip: 'rgba(129, 140, 248, 0.22)',
      border: 'rgba(165, 180, 252, 0.45)'
    },
    summary:
      'Start with Intro to Building Circuits, then move into quantum programming. Students compose circuits visually — qubits as lines on a staff, gates placed like notes on a music scale — then run and read results before going deeper.',
    curriculum: [
      'Intro to Building Circuits — wires, gates & reading a circuit diagram',
      'From classical circuits to qubits (bits vs superposition)',
      'Circuit composer — gates on a “music staff” of qubit lines',
      'Common gates: H, X, CX / CNOT, measure & readout',
      'Small algorithms students can compose and explain',
      'Capstone circuit lab + checkpoint quiz'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon — Circuits intro → Quantum'
  },
  {
    id: 'data-science',
    title: 'Data Science',
    tabLabel: 'Data Science',
    status: 'coming_soon',
    accent: {
      tab: '#f43f5e',
      panel: 'linear-gradient(165deg, #9f1239 0%, #4c0519 55%, #1c1917 100%)',
      chip: 'rgba(244, 63, 94, 0.22)',
      border: 'rgba(251, 113, 133, 0.45)'
    },
    summary:
      'From messy tables to honest charts: cleaning, exploring, and communicating with data — with ethics and privacy woven through every unit.',
    curriculum: [
      'Asking good questions of a dataset',
      'Cleaning & joining tables (spreadsheet → SQL bridge)',
      'Descriptive stats students can explain',
      'Charts that inform (not mislead)',
      'Bias, sampling & privacy (FERPA-aware)',
      'Mini analysis project + review quiz'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon'
  },
  {
    id: 'governance',
    title: 'Cybersecurity GRC',
    tabLabel: 'GRC',
    status: 'coming_soon',
    accent: {
      tab: '#38bdf8',
      panel: 'linear-gradient(165deg, #0c4a6e 0%, #164e63 45%, #020617 100%)',
      chip: 'rgba(56, 189, 248, 0.22)',
      border: 'rgba(125, 211, 252, 0.45)'
    },
    summary:
      'Cybersecurity Governance, Risk, and Compliance (GRC) — a strategic framework that aligns security practices with business goals, manages digital risks, and satisfies regulatory requirements.',
    curriculum: [
      'Governance — leadership, policies & accountability',
      'Risk management — identify, assess & prioritize threats',
      'Compliance — GDPR, HIPAA, ISO 27001 & internal policy',
      'GRC lifecycle: Initiate → Inventory → Select',
      'Educate → Implement → Validate → Improve',
      'Capstone: map controls to mission & emerging threats'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon'
  },
  {
    id: 'soc-analyst',
    title: 'SOC Analyst',
    tabLabel: 'SOC Analyst',
    status: 'coming_soon',
    accent: {
      tab: '#f97316',
      panel: 'linear-gradient(165deg, #9a3412 0%, #7c2d12 45%, #1c1917 100%)',
      chip: 'rgba(249, 115, 22, 0.22)',
      border: 'rgba(251, 146, 60, 0.5)'
    },
    summary:
      'Entry Security Operations Center (SOC) analyst habits: triage alerts, read logs, follow playbooks, and write clear case notes. Maps to foundational DCWF / NICE Cyber Defense Analyst (511) skills — not a CAE Cyber Ops program. Heavy labs ship as VirtualBox appliances (student-hosted) to keep Enterceptor cloud cost low; Chromebook rooms use a browser-lite path with curated log packs.',
    curriculum: [
      'SOC basics — ticket → triage → escalate',
      'Log literacy — auth, web, and firewall-style logs',
      'Alerts that lie — true / false positive drills',
      'Network & host clues (reuse Linux pathway skills)',
      'Playbooks & incident response steps',
      'Case write-up — timeline, IOCs, handoff notes',
      'Capstone mini-shift — multiple alerts → one full report',
      'SOC Analyst Foundations assessment (80%+)'
    ],
    dashboardSections: [],
    levelRangeHint: 'Coming soon — browser-lite + VirtualBox lab appliance'
  }
];

/**
 * Skylite OS downloadable practice boxes (VirtualBox / VMware) — catalog + shelf art.
 * No Learning Center points. Original machines; exam chart is guidance only.
 * Downloads / SSO / billing are not wired yet — UI stubs only.
 */
export const SKYLITE_BOX_PRICE_TIERS = {
  standard: {
    id: 'standard',
    label: 'Standard',
    blurb: 'Single VM · Easy to lower-Medium system exploitation',
    yearUsd: 35,
    rental48hUsd: 7
  },
  advanced: {
    id: 'advanced',
    label: 'Advanced',
    blurb: 'Harder Medium · exploit mod, tunneling, or deeper chains',
    yearUsd: 55,
    rental48hUsd: 9
  },
  lab_set: {
    id: 'lab_set',
    label: 'Lab set',
    blurb: 'Multi-VM (e.g. AD / pivot) · import as a small lab',
    yearUsd: 89,
    rental48hUsd: 15
  }
};

/** Suggested study chart — not a purchase gate. Bundle discount is illustrative. */
export const SKYLITE_EXAM_PREP_CHART = {
  title: 'OSCP-style prep (suggested)',
  disclaimer:
    'Practice playground only — not OffSec, not exam credit, not CommandLine course points. Buy any mix; this chart is our recommendation.',
  tracks: [
    {
      id: 'foundations',
      label: 'Foundations',
      skillFocus: 'Linux / Windows foothold · enum → exploit → escalate',
      boxIds: ['red-river', 'hijack']
    },
    {
      id: 'web-access',
      label: 'Web & initial access',
      skillFocus: 'App misconfigs · shells · common service abuse',
      boxIds: ['email-crush']
    },
    {
      id: 'pivot-tunnel',
      label: 'Pivot & tunnel',
      skillFocus: 'Routing through a foothold · port forward habits',
      boxIds: ['tunnel-digger']
    },
    {
      id: 'grc-scenario',
      label: 'GRC scenario (optional)',
      skillFocus: 'Governance / risk storytelling — not exam AD practice',
      boxIds: ['governance']
    }
  ],
  bundle: {
    id: 'oscp-style-bundle',
    title: 'OSCP-style Prep Bundle',
    includesBoxIds: ['red-river', 'hijack', 'email-crush', 'tunnel-digger'],
    yearUsd: 149,
    rental48hUsd: 29,
    note: 'Illustrative ~25% off à la carte Standards/Advanced — finalize before launch.'
  }
};

export const SKYLITE_VINYL_BOXES = [
  {
    id: 'red-river',
    title: 'Red River',
    jacketSrc: '/skylite-boxes/red-river.png',
    tier: 'standard',
    difficulty: 'Easy',
    skills: ['Linux foothold', 'Service enum', 'Basic priv-esc'],
    summary:
      'Original Easy Linux playground: enumerate services, land a foothold, and practice clean notes. Download for VirtualBox or VMware when licensing ships — no CommandLine points.',
    status: 'coming_soon'
  },
  {
    id: 'hijack',
    title: 'Hijack',
    jacketSrc: '/skylite-boxes/hijack.png',
    tier: 'standard',
    difficulty: 'Easy–Medium',
    skills: ['Windows foothold', 'Misconfig abuse', 'Manual priv-esc'],
    summary:
      'Original Windows-leaning practice box for foothold and escalation habits without Insane rabbit holes. Suggested early on the OSCP-style chart; buy anytime.',
    status: 'coming_soon'
  },
  {
    id: 'tunnel-digger',
    title: 'Tunnel Digger',
    jacketSrc: '/skylite-boxes/tunnel-digger.png',
    tier: 'advanced',
    difficulty: 'Medium',
    skills: ['Pivoting', 'Port forward', 'Chaining hosts'],
    summary:
      'Practice routing traffic through a compromised node (pivot / tunnel habits). Advanced tier — still a free-choice download when available, not a gated level.',
    status: 'coming_soon'
  },
  {
    id: 'email-crush',
    title: 'Email Crush',
    jacketSrc: '/skylite-boxes/email-crush.png',
    tier: 'standard',
    difficulty: 'Easy–Medium',
    skills: ['Web / mail path', 'Initial access', 'Cred reuse'],
    summary:
      'Web-and-access flavored playground for phishing-adjacent and service footholds. Part of the suggested OSCP-style path; optional if you only want AD later.',
    status: 'coming_soon'
  },
  {
    id: 'governance',
    title: 'Cybersecurity GRC',
    jacketSrc: '/skylite-boxes/governance.png',
    tier: 'standard',
    difficulty: 'Scenario',
    skills: ['GRC storytelling', 'Policy / risk framing'],
    summary:
      'Scenario jacket for Cybersecurity GRC practice — not an OSCP AD substitute. Same shelf UX; download licensing will follow the playground model when ready.',
    status: 'coming_soon'
  }
];

export function getSkyliteBoxById(id) {
  return SKYLITE_VINYL_BOXES.find((b) => b.id === id) || null;
}

export function getSkyliteTier(tierId) {
  return SKYLITE_BOX_PRICE_TIERS[tierId] || SKYLITE_BOX_PRICE_TIERS.standard;
}

export function isPartnerMetroInstitution(nameOrSlug) {
  const s = String(nameOrSlug || '').toLowerCase();
  return s.includes('metro');
}

/**
 * Temporary — hide Cloud Security 101/201 for CommandLine video (Regna / LRSD / owner filming).
 * Keep in sync with server/lib/trainingPacks.js. Set false to restore.
 */
export const TEMP_HIDE_CLOUD_SECURITY_PACK = true;

const TEMP_CLOUD_SECURITY_HIDDEN_EMAILS = new Set([
  'regna.howard@lrsd.org',
  'james.houston@enterceptor.com'
]);

export function shouldTempHideCloudSecurityPack({
  email,
  institutionName,
  institutionSlug,
  isOwnerOrTester,
  forceHide
} = {}) {
  if (!TEMP_HIDE_CLOUD_SECURITY_PACK) return false;
  if (forceHide) return true;
  if (isOwnerOrTester) return true;
  const e = String(email || '')
    .trim()
    .toLowerCase();
  if (e && TEMP_CLOUD_SECURITY_HIDDEN_EMAILS.has(e)) return true;
  if (e.endsWith('@lrsd.org') || e.endsWith('@students.lrsd.org')) return true;
  const inst = `${institutionName || ''} ${institutionSlug || ''}`.toLowerCase();
  if (inst.includes('lrsd') || inst.includes('little rock')) return true;
  return false;
}

/**
 * Packs unlocked for an institution. Metro partner always gets Cloud Security until further notice.
 * Owners/testers get Cloud Security for preview. Future DB entitlements can append here.
 */
export function resolveEnabledTrainingPacks({
  institutionName,
  institutionSlug,
  purchasedPackIds,
  isOwnerOrTester,
  email,
  forceHideCloudSecurity
} = {}) {
  const set = new Set(Array.isArray(purchasedPackIds) ? purchasedPackIds : []);
  if (
    isPartnerMetroInstitution(institutionName) ||
    isPartnerMetroInstitution(institutionSlug) ||
    isOwnerOrTester
  ) {
    set.add('cloud-security');
  }
  if (
    shouldTempHideCloudSecurityPack({
      email,
      institutionName,
      institutionSlug,
      isOwnerOrTester,
      forceHide: forceHideCloudSecurity
    })
  ) {
    set.delete('cloud-security');
  }
  return Array.from(set);
}
